import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play, Pause, SkipBack, SkipForward, Scissors, Merge,
  Trash2, Plus, Undo2, Redo2, Keyboard, Check, X, Eye, EyeOff,
} from 'lucide-react';

/* ────────────────────────────────────────────────
   工具函式
   ──────────────────────────────────────────────── */

function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/
  );
  return m ? m[1] : null;
}

// mm:ss.d（編輯器需要小數點精度）
export function fmtTime(sec) {
  if (sec == null || isNaN(sec)) return '00:00.0';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const d = Math.floor((sec % 1) * 10);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${d}`;
}

// SRT 時間格式 00:00:00,000
function srtTime(sec) {
  if (sec == null || isNaN(sec)) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  const ms = Math.round((sec % 1) * 1000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')},${String(ms).padStart(3, '0')}`;
}

export function buildSrt(segments) {
  return segments
    .map((seg, i) => {
      const spk = seg.speaker ? `[${seg.speaker}] ` : '';
      return `${i + 1}\n${srtTime(seg.start)} --> ${srtTime(seg.end)}\n${spk}${seg.text}\n`;
    })
    .join('\n');
}

export function buildTxt(segments) {
  return segments
    .map((seg) => (seg.speaker ? `[${seg.speaker}] ${seg.text}` : seg.text))
    .join('\n');
}

// 說話者色票（靛藍系為主，其餘作區隔）
const SPEAKER_COLORS = {
  A: { dot: 'bg-indigo-500',  chip: 'bg-indigo-100 text-indigo-700',   ring: 'ring-indigo-400' },
  B: { dot: 'bg-emerald-500', chip: 'bg-emerald-100 text-emerald-700', ring: 'ring-emerald-400' },
  C: { dot: 'bg-amber-500',   chip: 'bg-amber-100 text-amber-700',     ring: 'ring-amber-400' },
  D: { dot: 'bg-rose-500',    chip: 'bg-rose-100 text-rose-700',       ring: 'ring-rose-400' },
  E: { dot: 'bg-violet-500',  chip: 'bg-violet-100 text-violet-700',   ring: 'ring-violet-400' },
  F: { dot: 'bg-teal-500',    chip: 'bg-teal-100 text-teal-700',       ring: 'ring-teal-400' },
};

function speakerColor(speaker) {
  if (!speaker) return null;
  return SPEAKER_COLORS[speaker.slice(-1)] || SPEAKER_COLORS.A;
}

// 穩定的列 key：AnimatePresence 的 key 不能綁 start/index，
// 否則調整時間會被當成「刪除+新增」，退場動畫殘影會蓋住新值。
let _uid = 0;
const nextUid = () => `seg_${++_uid}`;

// 確保每一句都有 uid（來自後端的 segments 沒有）
function withUid(list) {
  return (list || []).map((s) => (s.uid ? s : { ...s, uid: nextUid() }));
}

const MAX_HISTORY = 50;

/* ────────────────────────────────────────────────
   影片上的字幕疊加層
   ──────────────────────────────────────────────── */

function SubtitleOverlay({ seg, style, visible, bottomOffset = 16 }) {
  if (!visible || !seg || !seg.text) return null;
  return (
    <div
      className="absolute left-0 right-0 flex justify-center px-6 pointer-events-none z-10"
      style={{ bottom: bottomOffset }}
    >
      <span
        className="inline-block text-center font-bold leading-snug max-w-[92%]"
        style={{
          fontSize: `${style.size}px`,
          color: '#fff',
          // 描邊 + 陰影：不論影片深淺底都看得清楚
          textShadow:
            '0 0 4px rgba(0,0,0,.9), 0 2px 6px rgba(0,0,0,.8), ' +
            '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          background: style.box ? 'rgba(0,0,0,.62)' : 'transparent',
          padding: style.box ? '0.25em 0.7em' : 0,
          borderRadius: style.box ? '6px' : 0,
        }}
      >
        {seg.speaker ? `${seg.speaker}：${seg.text}` : seg.text}
      </span>
    </div>
  );
}

// 把新版本推入歷史堆疊，統一處理截斷與索引同步
function pushHistory(h, next) {
  const trimmed = [...h.stack.slice(0, h.idx + 1), next];
  const overflow = Math.max(0, trimmed.length - MAX_HISTORY);
  const stack = overflow ? trimmed.slice(overflow) : trimmed;
  return { stack, idx: stack.length - 1 };
}

/* ────────────────────────────────────────────────
   SubtitleEditor
   ──────────────────────────────────────────────── */

export default function SubtitleEditor({ file, url, segments: initialSegments, onChange }) {
  // ── 編輯狀態（需先於 undo/redo 宣告）──────────
  const [editingIdx, setEditingIdx] = useState(-1);
  const [draft, setDraft] = useState('');

  // ── 編輯歷史（undo / redo）────────────────────
  // history 與 histIdx 必須在同一次更新中一起算，否則截斷（slice(-50)）
  // 會讓兩者對不上，導致 history[histIdx] 變成 undefined 而整個元件崩潰。
  const [hist, setHist] = useState(() => ({ stack: [withUid(initialSegments)], idx: 0 }));
  // 用 useMemo 穩定參考，否則每次 render 都產生新陣列，
  // 會讓依賴 segments 的 useEffect / useMemo 無限重跑
  const segments = useMemo(() => hist.stack[hist.idx] || [], [hist]);
  const history = hist.stack;
  const histIdx = hist.idx;

  const commit = useCallback((next) => {
    setHist((h) => pushHistory(h, next));
  }, []);

  // undo/redo 會整批換掉 segments，editingIdx 可能越界，故一併退出編輯
  const undo = useCallback(() => {
    setEditingIdx(-1);
    setHist((h) => ({ ...h, idx: Math.max(0, h.idx - 1) }));
  }, []);
  const redo = useCallback(() => {
    setEditingIdx(-1);
    setHist((h) => ({ ...h, idx: Math.min(h.stack.length - 1, h.idx + 1) }));
  }, []);

  useEffect(() => { onChange && onChange(segments); }, [segments, onChange]);

  // 換一支新影片／重新轉錄時，initialSegments 會變，但 useState 初始值
  // 只在首次掛載生效，必須手動重設歷史，否則會停留在上一次的字幕。
  useEffect(() => {
    setHist({ stack: [withUid(initialSegments)], idx: 0 });
    setEditingIdx(-1);
  }, [initialSegments]);

  // ── 播放器狀態 ────────────────────────────────
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  // 影片字幕疊加
  const [showSubs, setShowSubs] = useState(true);
  const [subStyle, setSubStyle] = useState({ size: 22, box: true });

  const mediaRef = useRef(null);
  const ytPlayerRef = useRef(null);
  const ytIntervalRef = useRef(null);
  const ytContainerRef = useRef(null);
  const rowRefs = useRef([]);
  const listRef = useRef(null);
  const editInputRef = useRef(null);

  const ytId = extractYouTubeId(url);
  const isVideo = file && file.type && file.type.startsWith('video/');

  // ── blob URL ─────────────────────────────────
  useEffect(() => {
    if (file) {
      const u = URL.createObjectURL(file);
      setBlobUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    setBlobUrl(null);
  }, [file]);

  // ── YouTube IFrame API ───────────────────────
  useEffect(() => {
    if (!ytId) return;
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }

    // 輪詢與 onReady 解耦：onYouTubeIframeAPIReady 是全域單一變數，
    // 頁面上若有其他元件也用它就會互相覆蓋，導致 onReady 不觸發、
    // currentTime 永遠停在 0、字幕疊加不會動。改為只要拿得到
    // getCurrentTime 就開始取樣，並在此同步 duration。
    const startPolling = () => {
      clearInterval(ytIntervalRef.current);
      ytIntervalRef.current = setInterval(() => {
        const p = ytPlayerRef.current;
        if (!p || !p.getCurrentTime) return;
        try {
          setCurrentTime(p.getCurrentTime() || 0);
          const d = p.getDuration ? p.getDuration() : 0;
          if (d) setDuration((prev) => (Math.abs(prev - d) > 0.5 ? d : prev));
          if (p.getPlayerState) setPlaying(p.getPlayerState() === 1);
        } catch { /* player 尚未就緒 */ }
      }, 100);   // 字幕疊加需要較密的取樣，200ms 會有可見延遲
    };

    const init = () => {
      if (!ytContainerRef.current) return;
      ytPlayerRef.current = new window.YT.Player(ytContainerRef.current, {
        videoId: ytId,
        playerVars: { autoplay: 0, rel: 0 },
        events: {
          onReady: (e) => {
            setDuration(e.target.getDuration() || 0);
            startPolling();
          },
          onStateChange: (e) => setPlaying(e.data === 1),
        },
      });
      startPolling();   // 保險：不等 onReady
    };

    if (window.YT && window.YT.Player) {
      init();
    } else {
      // 保留既有的 handler，避免覆蓋其他元件的初始化
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === 'function') prev();
        init();
      };
    }

    return () => {
      clearInterval(ytIntervalRef.current);
      if (ytPlayerRef.current && ytPlayerRef.current.destroy) {
        ytPlayerRef.current.destroy();
        ytPlayerRef.current = null;
      }
    };
  }, [ytId]);

  // ── 目前所在字幕 ──────────────────────────────
  const activeIdx = useMemo(
    () => segments.findIndex((s) => currentTime >= s.start && currentTime < s.end),
    [currentTime, segments]
  );

  // ── 自動捲動 ─────────────────────────────────
  useEffect(() => {
    if (!autoScroll || editingIdx >= 0) return;
    if (activeIdx >= 0 && rowRefs.current[activeIdx]) {
      rowRefs.current[activeIdx].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeIdx, autoScroll, editingIdx]);

  // ── 播放控制 ─────────────────────────────────
  const seekTo = useCallback((t) => {
    const time = Math.max(0, t);
    if (mediaRef.current) mediaRef.current.currentTime = time;
    else if (ytPlayerRef.current && ytPlayerRef.current.seekTo) ytPlayerRef.current.seekTo(time, true);
    setCurrentTime(time);
  }, []);

  const togglePlay = useCallback(() => {
    if (mediaRef.current) {
      if (mediaRef.current.paused) { mediaRef.current.play(); setPlaying(true); }
      else { mediaRef.current.pause(); setPlaying(false); }
    } else if (ytPlayerRef.current) {
      const st = ytPlayerRef.current.getPlayerState && ytPlayerRef.current.getPlayerState();
      if (st === 1) { ytPlayerRef.current.pauseVideo(); setPlaying(false); }
      else { ytPlayerRef.current.playVideo(); setPlaying(true); }
    }
  }, []);

  // ── 編輯操作 ─────────────────────────────────
  const startEdit = (i) => {
    const seg = segments[i];
    if (!seg) return;                 // 索引可能因刪除/合併而失效
    setEditingIdx(i);
    setDraft(seg.text || '');
  };

  const saveEdit = () => {
    if (editingIdx < 0 || !segments[editingIdx]) { setEditingIdx(-1); return; }
    const next = segments.map((s, i) => (i === editingIdx ? { ...s, text: draft } : s));
    commit(next);
    setEditingIdx(-1);
  };

  const cancelEdit = () => setEditingIdx(-1);

  // 刪除／合併會讓 editingIdx 指向別句甚至越界，一律先退出編輯
  const removeSeg = (i) => {
    setEditingIdx(-1);
    commit(segments.filter((_, k) => k !== i));
  };

  // 時間微調：用 functional update 讀取最新的 hist，
  // 否則連續快速點擊會全部基於同一份舊 state，只生效一次
  const shiftTime = (i, field, delta) => {
    setHist((h) => {
      const cur = h.stack[h.idx] || [];
      const s = cur[i];
      if (!s) return h;
      const v = Math.max(0, +(s[field] + delta).toFixed(2));
      if (field === 'start' && v >= s.end) return h;
      if (field === 'end' && v <= s.start) return h;
      const next = cur.map((seg, k) => (k === i ? { ...seg, [field]: v } : seg));
      return pushHistory(h, next);
    });
  };

  const setTimeToCurrent = (i, field) => {
    const next = segments.map((s, k) => {
      if (k !== i) return s;
      const v = +currentTime.toFixed(2);
      if (field === 'start' && v >= s.end) return s;
      if (field === 'end' && v <= s.start) return s;
      return { ...s, [field]: v };
    });
    commit(next);
  };

  const mergeWithNext = (i) => {
    if (i >= segments.length - 1) return;
    setEditingIdx(-1);
    const a = segments[i], b = segments[i + 1];
    const merged = { ...a, end: b.end, text: `${a.text} ${b.text}`.trim() };
    commit([...segments.slice(0, i), merged, ...segments.slice(i + 2)]);
  };

  const splitSeg = (i) => {
    const s = segments[i];
    // 以游標位置切；若不在此段內則取中點
    const cut = currentTime > s.start && currentTime < s.end
      ? +currentTime.toFixed(2)
      : +((s.start + s.end) / 2).toFixed(2);
    const words = s.text.split(/\s+/);
    const half = Math.ceil(words.length / 2);
    const a = { ...s, end: cut, text: words.slice(0, half).join(' ') };
    const b = { ...s, id: undefined, uid: nextUid(), start: cut, text: words.slice(half).join(' ') };
    commit([...segments.slice(0, i), a, b, ...segments.slice(i + 1)]);
  };

  const insertAfter = (i) => {
    const s = segments[i];
    if (!s) return;
    const gapEnd = i < segments.length - 1 ? segments[i + 1].start : s.end + 2;
    const nb = { uid: nextUid(), start: s.end, end: Math.max(s.end + 1, Math.min(gapEnd, s.end + 2)), text: '' };
    commit([...segments.slice(0, i + 1), nb, ...segments.slice(i + 1)]);
    // 直接進入編輯：新句必為空字串，不可用 setTimeout 回頭讀 segments
    // （那是插入前的舊陣列，索引會越界）
    setEditingIdx(i + 1);
    setDraft('');
  };

  // ── 鍵盤快速鍵 ────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const typing = ['INPUT', 'TEXTAREA'].includes(e.target.tagName);
      if (typing) {
        if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); saveEdit(); }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault(); e.shiftKey ? redo() : undo(); return;
      }
      switch (e.key) {
        case ' ':          e.preventDefault(); togglePlay(); break;
        case 'ArrowLeft':  e.preventDefault(); seekTo(currentTime - (e.shiftKey ? 1 : 5)); break;
        case 'ArrowRight': e.preventDefault(); seekTo(currentTime + (e.shiftKey ? 1 : 5)); break;
        case 'ArrowUp':    e.preventDefault(); if (activeIdx > 0) seekTo(segments[activeIdx - 1].start); break;
        case 'ArrowDown':  e.preventDefault(); if (activeIdx >= 0 && activeIdx < segments.length - 1) seekTo(segments[activeIdx + 1].start); break;
        case 'Enter':      if (activeIdx >= 0) { e.preventDefault(); startEdit(activeIdx); } break;
        case '?':          setShowHelp((v) => !v); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentTime, activeIdx, segments, togglePlay, seekTo, undo, redo]); // eslint-disable-line react-hooks/exhaustive-deps

  // 進入編輯自動聚焦
  useEffect(() => {
    if (editingIdx >= 0 && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingIdx]);

  const dirty = histIdx > 0;
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  /* ── Render ─────────────────────────────────── */
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="bg-white rounded-card border border-brand-border/60 shadow-card overflow-hidden"
    >
      {/* 標題列 */}
      <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-brand-border/40 bg-paper">
        <h3 className="font-bold flex items-center gap-2">
          <span>{ytId ? '🎬' : isVideo ? '🎥' : '🎧'}</span>
          <span>字幕編輯器</span>
          <AnimatePresence>
            {dirty && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-pill"
              >
                已修改
              </motion.span>
            )}
          </AnimatePresence>
        </h3>
        <div className="flex items-center gap-1.5">
          <button
            onClick={undo} disabled={histIdx === 0} title="復原 (Ctrl+Z)"
            className="p-2 rounded-pill hover:bg-brand-soft disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          ><Undo2 size={15} /></button>
          <button
            onClick={redo} disabled={histIdx >= history.length - 1} title="重做 (Ctrl+Shift+Z)"
            className="p-2 rounded-pill hover:bg-brand-soft disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          ><Redo2 size={15} /></button>
          <button
            onClick={() => setShowHelp((v) => !v)} title="快速鍵 (?)"
            className={`p-2 rounded-pill transition-colors ${showHelp ? 'bg-brand text-white' : 'hover:bg-brand-soft'}`}
          ><Keyboard size={15} /></button>
        </div>
      </div>

      {/* 快速鍵說明 */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden bg-ink text-white"
          >
            <div className="px-5 sm:px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-xs">
              {[
                ['空白鍵', '播放 / 暫停'],
                ['← →', '倒退 / 快轉 5 秒'],
                ['Shift + ← →', '微調 1 秒'],
                ['↑ ↓', '上 / 下一句'],
                ['Enter', '編輯目前這句'],
                ['Ctrl + Enter', '儲存編輯'],
                ['Esc', '取消編輯'],
                ['Ctrl + Z', '復原 / 重做'],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <kbd className="bg-white/15 px-2 py-0.5 rounded font-mono text-[10px] whitespace-nowrap">{k}</kbd>
                  <span className="text-white/70">{v}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 播放器（含字幕疊加）*/}
      <div className="px-5 sm:px-6 pt-5">
        {ytId ? (
          <div className="relative rounded-card overflow-hidden bg-black">
            <div ref={ytContainerRef} className="w-full aspect-video" />
            {/* YouTube 控制列約 50px 高，字幕往上讓開 */}
            <SubtitleOverlay seg={segments[activeIdx]} style={subStyle} visible={showSubs} bottomOffset={56} />
          </div>
        ) : blobUrl && isVideo ? (
          <div className="relative rounded-card overflow-hidden bg-black">
            <video
              ref={mediaRef} src={blobUrl} controls
              onTimeUpdate={() => mediaRef.current && setCurrentTime(mediaRef.current.currentTime)}
              onLoadedMetadata={() => mediaRef.current && setDuration(mediaRef.current.duration)}
              onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
              className="w-full aspect-video"
            />
            {/* controls 列約 40px，字幕往上讓開避免被蓋住 */}
            <SubtitleOverlay seg={segments[activeIdx]} style={subStyle} visible={showSubs} bottomOffset={48} />
          </div>
        ) : blobUrl ? (
          <>
            <audio
              ref={mediaRef} src={blobUrl} controls
              onTimeUpdate={() => mediaRef.current && setCurrentTime(mediaRef.current.currentTime)}
              onLoadedMetadata={() => mediaRef.current && setDuration(mediaRef.current.duration)}
              onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
              className="w-full"
            />
            {/* 純音訊：用深色面板顯示目前字幕，效果等同影片疊加 */}
            {showSubs && (
              <div className="mt-3 bg-ink rounded-card px-5 py-6 text-center min-h-[5.5rem] flex items-center justify-center">
                <p
                  className="text-white font-bold leading-snug"
                  style={{ fontSize: `${subStyle.size}px`, textShadow: '0 2px 8px rgba(0,0,0,.6)' }}
                >
                  {segments[activeIdx] ? segments[activeIdx].text : <span className="text-white/30 text-base">（播放時顯示字幕）</span>}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-card bg-paper border border-brand-border/40 py-8 text-center text-sm text-ink-muted">
            找不到可播放的媒體（YouTube 轉錄後仍可編輯字幕）
          </div>
        )}
      </div>

      {/* 時間軸 + 傳輸控制 */}
      <div className="px-5 sm:px-6 pt-4">
        <div
          className="relative h-9 bg-paper rounded-pill overflow-hidden cursor-pointer border border-brand-border/40"
          onClick={(e) => {
            if (!duration) return;
            const r = e.currentTarget.getBoundingClientRect();
            seekTo(((e.clientX - r.left) / r.width) * duration);
          }}
          title="點擊跳轉"
        >
          {/* 每個字幕在時間軸上的位置 */}
          {duration > 0 && segments.map((s, i) => (
            <div
              key={i}
              className={`absolute top-0 bottom-0 ${i === activeIdx ? 'bg-brand/45' : 'bg-brand/15'} transition-colors`}
              style={{ left: `${(s.start / duration) * 100}%`, width: `${Math.max(0.4, ((s.end - s.start) / duration) * 100)}%` }}
            />
          ))}
          {/* 播放頭 */}
          <motion.div
            className="absolute top-0 bottom-0 w-0.5 bg-brand"
            animate={{ left: `${progressPct}%` }}
            transition={{ duration: 0.15, ease: 'linear' }}
          />
        </div>

        <div className="flex items-center justify-between gap-3 mt-3">
          <div className="flex items-center gap-1.5">
            <button onClick={() => seekTo(currentTime - 5)} className="p-2 rounded-pill hover:bg-brand-soft transition-colors" title="倒退 5 秒">
              <SkipBack size={16} />
            </button>
            <button onClick={togglePlay} className="p-2.5 rounded-pill bg-brand text-white hover:bg-brand-hover transition-colors" title="播放 / 暫停（空白鍵）">
              {playing ? <Pause size={16} /> : <Play size={16} />}
            </button>
            <button onClick={() => seekTo(currentTime + 5)} className="p-2 rounded-pill hover:bg-brand-soft transition-colors" title="快轉 5 秒">
              <SkipForward size={16} />
            </button>
            <span className="ml-2 text-xs font-mono text-ink-muted tabular-nums">
              {fmtTime(currentTime)} / {fmtTime(duration)}
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* 字幕疊加控制 */}
            <div className="flex items-center gap-1 bg-paper rounded-pill px-2 py-1">
              <button
                onClick={() => setShowSubs((v) => !v)}
                className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-pill transition-colors ${
                  showSubs ? 'bg-brand text-white' : 'text-ink-muted hover:bg-white'
                }`}
                title="在影片上顯示 / 隱藏字幕"
              >
                {showSubs ? <Eye size={13} /> : <EyeOff size={13} />} 字幕
              </button>
              <button
                onClick={() => setSubStyle((s) => ({ ...s, size: Math.max(14, s.size - 2) }))}
                disabled={!showSubs}
                className="w-6 text-xs font-bold text-ink-muted hover:text-brand disabled:opacity-30 transition-colors"
                title="縮小字幕"
              >A−</button>
              <button
                onClick={() => setSubStyle((s) => ({ ...s, size: Math.min(40, s.size + 2) }))}
                disabled={!showSubs}
                className="w-6 text-xs font-bold text-ink-muted hover:text-brand disabled:opacity-30 transition-colors"
                title="放大字幕"
              >A+</button>
              <button
                onClick={() => setSubStyle((s) => ({ ...s, box: !s.box }))}
                disabled={!showSubs}
                className={`text-[10px] font-bold px-2 py-1 rounded-pill transition-colors disabled:opacity-30 ${
                  subStyle.box ? 'bg-ink text-white' : 'text-ink-muted hover:bg-white'
                }`}
                title="切換字幕底色框"
              >底框</button>
            </div>

            <label className="flex items-center gap-2 text-xs font-bold cursor-pointer select-none">
              <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} className="w-3.5 h-3.5" />
              <span className="text-ink-muted">自動捲動</span>
            </label>
          </div>
        </div>
      </div>

      {/* 字幕列表 */}
      <div className="px-5 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-bold tracking-[0.18em] text-ink-muted">
            字幕 {segments.length} 句
          </p>
          <p className="text-[11px] text-ink-muted">點擊跳轉・雙擊編輯・按 ? 看快速鍵</p>
        </div>

        {/* 純 div，不用 motion/layout：
            layout 動畫在頻繁增刪時會讓相鄰列的位移互相干擾而視覺重疊，
            且高度變化期間 DOM 位置與實際不符。列表求穩定優先於動效。 */}
        <div ref={listRef} className="max-h-[30rem] overflow-y-auto rounded-card border border-brand-border/40">
          {segments.map((seg, i) => {
            const active = i === activeIdx;
            const spk = speakerColor(seg.speaker);
            const editing = i === editingIdx;
            return (
              <div
                key={seg.uid}
                ref={(el) => (rowRefs.current[i] = el)}
                onClick={() => !editing && seekTo(seg.start)}
                onDoubleClick={() => startEdit(i)}
                className={`group relative border-b border-brand-border/25 last:border-b-0 px-4 py-4 cursor-pointer transition-colors ${
                  active ? 'bg-brand-soft' : 'hover:bg-paper'
                }`}
              >
                {active && <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand" />}

                {/* 第一行：序號 + 時間 + 說話者 + 操作 */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="shrink-0 text-[10px] font-mono text-ink-muted/50 w-6">
                    {String(i + 1).padStart(2, '0')}
                  </span>

                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); shiftTime(i, 'start', -0.1); }}
                      className="opacity-0 group-hover:opacity-100 text-ink-muted hover:text-brand w-4 text-xs transition-opacity"
                      title="起始時間 -0.1s"
                    >−</button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setTimeToCurrent(i, 'start'); }}
                      className={`text-[11px] font-mono tabular-nums px-1.5 py-0.5 rounded hover:bg-brand hover:text-white transition-colors ${
                        active ? 'text-brand font-bold' : 'text-ink-muted'
                      }`}
                      title="設為目前播放時間"
                    >{fmtTime(seg.start)}</button>
                    <button
                      onClick={(e) => { e.stopPropagation(); shiftTime(i, 'start', 0.1); }}
                      className="opacity-0 group-hover:opacity-100 text-ink-muted hover:text-brand w-4 text-xs transition-opacity"
                      title="起始時間 +0.1s"
                    >+</button>
                  </div>

                  <span className="text-ink-muted/40 text-[11px]">→</span>

                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); shiftTime(i, 'end', -0.1); }}
                      className="opacity-0 group-hover:opacity-100 text-ink-muted hover:text-brand w-4 text-xs transition-opacity"
                      title="結束時間 -0.1s"
                    >−</button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setTimeToCurrent(i, 'end'); }}
                      className="text-[11px] font-mono tabular-nums text-ink-muted px-1.5 py-0.5 rounded hover:bg-brand hover:text-white transition-colors"
                      title="設為目前播放時間"
                    >{fmtTime(seg.end)}</button>
                    <button
                      onClick={(e) => { e.stopPropagation(); shiftTime(i, 'end', 0.1); }}
                      className="opacity-0 group-hover:opacity-100 text-ink-muted hover:text-brand w-4 text-xs transition-opacity"
                      title="結束時間 +0.1s"
                    >+</button>
                  </div>

                  {seg.speaker && spk && (
                    <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-pill ${spk.chip}`}>
                      {seg.speaker}
                    </span>
                  )}

                  {!editing && (
                    <div className="ml-auto shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => { e.stopPropagation(); splitSeg(i); }}
                        className="p-1.5 rounded hover:bg-white text-ink-muted hover:text-brand transition-colors" title="從播放位置分割">
                        <Scissors size={14} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); mergeWithNext(i); }} disabled={i >= segments.length - 1}
                        className="p-1.5 rounded hover:bg-white text-ink-muted hover:text-brand disabled:opacity-20 transition-colors" title="與下一句合併">
                        <Merge size={14} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); insertAfter(i); }}
                        className="p-1.5 rounded hover:bg-white text-ink-muted hover:text-brand transition-colors" title="在後方插入一句">
                        <Plus size={14} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); removeSeg(i); }}
                        className="p-1.5 rounded hover:bg-white text-ink-muted hover:text-rose-500 transition-colors" title="刪除這句">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {/* 第二行：字幕文字（獨佔整行，不再被時間擠壓）*/}
                {editing ? (
                  <div onClick={(e) => e.stopPropagation()} className="pl-8">
                    <textarea
                      ref={editInputRef}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      rows={2}
                      className="w-full text-[15px] leading-relaxed border border-brand rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand/25 outline-none resize-none"
                    />
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <button onClick={saveEdit} className="flex items-center gap-1 text-xs font-bold bg-brand text-white px-3 py-1.5 rounded-pill hover:bg-brand-hover transition-colors">
                        <Check size={13} /> 儲存
                      </button>
                      <button onClick={cancelEdit} className="flex items-center gap-1 text-xs font-bold bg-paper text-ink-muted px-3 py-1.5 rounded-pill hover:bg-brand-soft transition-colors">
                        <X size={13} /> 取消
                      </button>
                      <span className="text-[10px] text-ink-muted">Ctrl+Enter 儲存・Esc 取消</span>
                    </div>
                  </div>
                ) : (
                  <p className={`pl-8 text-[15px] leading-relaxed break-words ${
                    active ? 'text-ink font-medium' : 'text-ink/80'
                  } ${!seg.text ? 'italic text-ink-muted' : ''}`}>
                    {seg.text || '（空白，雙擊輸入）'}
                  </p>
                )}
              </div>
            );
          })}

        {segments.length === 0 && (
            <div className="py-10 text-center text-sm text-ink-muted">尚無字幕</div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
